import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import ApexCharts from 'apexcharts';
import { Button } from "@mui/material";

const EboilerGraph = ({ state, fullscreenGraphs, setFullscreenGraphs, afrrReservations }) => {
    const [originalState, setOriginalState] = useState(state);
    const [zoomRange, setZoomRange] = useState(null);
    useEffect(() => {

        const chart = ApexCharts.getChartByID("eboiler");
        setOriginalState({
            minx: chart.w.config.xaxis.min, maxx: chart.w.config.xaxis.max,
            miny: chart.w.config.yaxis.min, maxy: chart.w.config.yaxis.max
        })

    }, [zoomRange]);
   
    // Get the time range from state data
    const stateTimeRange = state && state.series && state.series.length > 0 && state.series[0].data.length > 0 ? {
        min: Math.min(...state.series[0].data.map(point => point.x)),
        max: Math.max(...state.series[0].data.map(point => point.x))
    } : null;
    
    
    // Filter afrrReservations to only include data within the state's time range
    const filteredAfrrReservations = afrrReservations && stateTimeRange ? 
        afrrReservations.filter(item => {
            const itemFromTime = new Date(item.from).getTime();
            const itemToTime = new Date(item.to).getTime();
            // Include if the reservation overlaps with the state time range
            return itemFromTime <= stateTimeRange.max && itemToTime >= stateTimeRange.min;
        }) : [];
    
    
    // Convert filteredAfrrReservations to data points matching state data timestamps
    const convertToHourlyData = (reservations, stateTimeRange, stateData) => {
        if (!reservations || !stateTimeRange || !stateData) return [];
        
        const afrrData = [];
        
    
        
        // Use the exact same timestamps as the state data (including seconds)
        stateData.forEach(statePoint => {
            const currentTimestamp = statePoint.x;
            
            // Find the reservation that covers this timestamp
            const coveringReservation = reservations.find(reservation => {
                const reservationStart = new Date(reservation.from).getTime();
                const reservationEnd = new Date(reservation.to).getTime();
                return currentTimestamp >= reservationStart && currentTimestamp < reservationEnd;
            });
            
            // Add data point with the reservation value or 0 if no reservation
            afrrData.push({
                x: currentTimestamp,
                y: coveringReservation ? parseInt(coveringReservation.afrr_down_capacity_reserved) : 0
            });
        });
        
     
        
        return afrrData;
    };
    
    // Prepare afrrReservations data for rendering
    const afrrSeries = stateTimeRange && state.series && state.series.length > 0 ? [{
        name: "aFRR Down",
        data: convertToHourlyData(filteredAfrrReservations, stateTimeRange, state.series[0].data),
        type: 'area',
        color: "#E78B48",
        stroke: {
            curve: 'stepline',
            width: 3,
            dashArray: 5, // Dashed line to distinguish from Eboiler
        },
        fill: {
            type: "gradient",
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.6,
                opacityTo: 0.2,
                stops: [0, 90, 100],
                colorStops: [
                    {
                        offset: 0,
                        color: "#E78B48",
                        opacity: 0.6
                    },
                    {
                        offset: 100,
                        color: "#E78B48",
                        opacity: 0.2
                    }
                ]
            }
        }
    }] : [];

    const commonOptions = {
        chart: {
            id: "eboiler",
            type: "area",
            height: '100%',
            width: '100%',
            zoom: {
                enabled: true,
                type: "xy",
                autoScaleYaxis: false,
                allowMouseWheelZoom: false,

            },
            toolbar: {
                autoSelected: "zoom",
                tools: {
                    download: true,
                    selection: true,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: true,
                    reset: true,
                    customIcons: [{
                        icon: '<i class="fa fa-expand"></i>',
                        index: 0,
                        title: 'Fullscreen',
                        class: 'custom-icon',
                        click: function (chart, options, e) {
                            setFullscreenGraphs(!fullscreenGraphs);
                        }
                    }]
                },
            },
            events: {
                zoomed: function (chartContext, { xaxis }) {
                    setZoomRange({ min: xaxis.min, max: xaxis.max });
                },
                beforeResetZoom: function (chartContext, { xaxis }) {
                    //ApexCharts.exec("eboiler", "resetZoom");
                    //ApexCharts.exec('eboiler', 'zoomX', originalState.minx, originalState.maxx);
                    //ApexCharts.exec('eboiler', 'zoomY', originalState.miny, originalState.maxy);
                    ApexCharts.exec("eboiler", "updateOptions", {
                        xaxis: { min: undefined, max: undefined },
                        yaxis: { min: undefined, max: undefined },
                    }, false, true);
                },
            },
        },
        toolbar: {
            show: true,
            tools: {
                download: true,
                selection: true,
                zoom: true,
                zoomin: true,
                zoomout: true,
                pan: true,
                reset: true,
                customIcons: [{
                    icon: '<i class="fa fa-expand" ></i>',
                    index: 0,
                    title: 'Fullscreen',
                    class: 'custom-icon',
                    click: function (chart, options, e) {
                        setFullscreenGraphs(!fullscreenGraphs);
                    }
                }]
            },
        },
        dataLabels: {
            enabled: false, // Hide numbers on data points
        },
        markers: {
            size: 0, // Remove point markers
        },
        stroke: {
            curve: 'stepline',
            width: 2,
            dashArray: 0 // Solid line for Eboiler
        },
        colors: ['#1F9562'],
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                inverseColors: false,
                opacityFrom: 0.5,
                opacityTo: 0,
                stops: [0, 90, 100]
            },
        },
        yaxis: {
            min: 0,
            max: 1500,
            title: {
                text: 'Eboiler power (kW)',
                style: {
                    fontSize: '11px',
                    fontWeight: 400,
                    fontFamily: 'Helvetica, Arial, sans-serif',

                }
            },
        },
        xaxis: {
            type: 'datetime',
            labels: {
                datetimeUTC: false,  // This makes it use local time
                format: 'dd/MM/yyyy HH:mm:ss',
                style: {
                    fontSize: '11px'
                },
                rotate: -45,
                rotateAlways: false,
            },
            tooltip: {
                formatter: function (val) {
                    return new Date(val).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    });
                }
            }
        },
        tooltip: {
            enabled: true,
            followCursor: true,    // Disable follow cursor
            intersect: false,       // Show tooltip even when not directly over point
            shared: true,           // Show all series in tooltip
            custom: undefined,      // Remove any custom tooltip
            fixed: {
                enabled: true,      // Enable fixed position
                position: 'topLeft', // Position the tooltip
                offsetX: 10,        // Add some horizontal offset
                offsetY: 10,        // Add some vertical offset
            },
            x: {
                format: 'dd MMM yyyy HH:mm:ss'
            },
            marker: {
                show: true,
            },
            onDatasetHover: {
                highlightDataSeries: true,
            },
        },

    };



   

    return (
        <ReactApexChart
            options={{
                ...commonOptions,
                xaxis: {
                    ...commonOptions.xaxis,
                    ...(zoomRange && { min: zoomRange.min, max: zoomRange.max }),
                },
            }}
            series={[...state.series, ...afrrSeries]} // Combine original series with afrrReservations
            type="area"
            height="100%"
            width="100%"
        />
    );
};

export default EboilerGraph;
